import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { WifiOff } from "lucide-react";
import { apiClient } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useShownRoles } from "../../auth/useShownRoles";
import { screensFor } from "../hub/hubs";
import { useRelease150 } from "../../lib/release150";
import { isOnline, getOfflineSince, offlineBannerText, shownDataAt, useOnline } from "../../lib/phone/network";
import {
  DISPATCH_KEY,
  MY_WORK_KEY,
  OfflineKeeper,
  installOfflineWriteGuard,
  keptQueryKeys,
  neverQueueMutations,
  rememberWorkOrder,
  setWriteControlsDisabled,
  workOrderKeyFromPath
} from "../../lib/phone/offline";
import { sameOperation } from "../../lib/phone/scanCode";
import { BottomTabBar } from "./BottomTabBar";
import { usePhoneFeatures, usePhoneMode, usePhoneSheet } from "./phoneState";
import "./phone.css";

// Release 1.5.0 phone mode and offline read, mounted once by the app shell. With the 1.5.0 switch off (or for someone
// without an Operations role) it renders nothing and changes nothing.

const ScannerSheet = lazy(async () => ({ default: (await import("./ScannerSheet")).ScannerSheet }));
const MoreSheet = lazy(async () => ({ default: (await import("./MoreSheet")).MoreSheet }));
const ProblemReportSheet = lazy(async () => ({ default: (await import("./ProblemReportSheet")).ProblemReportSheet }));

/** Pages whose tables become stacked cards in the phone layout (wide print sheets like the router keep panning). */
export const STACKED_TABLE_ROUTES: readonly RegExp[] = [
  /^\/work-orders\/[^/]+$/,
  /^\/operations\/work-orders$/,
  /^\/shop-floor\/dispatch$/,
  /^\/shop-floor\/working-now$/,
  /^\/my\/work$/,
  /^\/quality\/incidents\/\d+$/
];

/** Gives every cell of the page's data tables its column name, so CSS can show each row as a card. */
export function labelTables(root: ParentNode): void {
  root.querySelectorAll<HTMLTableElement>("table.data-table").forEach((table) => {
    if (table.closest("[data-phone-keep-table]")) return;
    // Long lists draw only the rows in view (perf-1-5); their row maths need real table rows, so they keep panning.
    if (table.hasAttribute("aria-rowcount") || table.querySelector("tr[data-vindex]")) return;
    const headers = Array.from(table.querySelectorAll("thead th")).map((th) => (th.textContent ?? "").trim());
    table.setAttribute("data-phone-stack", "");
    table.querySelectorAll("tbody tr").forEach((tr) => {
      let col = 0;
      Array.from(tr.children).forEach((cell) => {
        if (!cell.hasAttribute("data-label")) cell.setAttribute("data-label", headers[col] ?? "");
        col += Number((cell as HTMLTableCellElement).colSpan) || 1;
      });
    });
  });
}

function useStackedTables(active: boolean, pathname: string) {
  useEffect(() => {
    if (!active || !STACKED_TABLE_ROUTES.some((r) => r.test(pathname))) return;
    const main = document.getElementById("main");
    if (!main) return;
    let frame = 0;
    const run = () => {
      frame = 0;
      labelTables(main);
    };
    run();
    const observer = new MutationObserver(() => {
      if (!frame) frame = window.requestAnimationFrame(run);
    });
    observer.observe(main, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      main.querySelectorAll("table[data-phone-stack]").forEach((t) => t.removeAttribute("data-phone-stack"));
    };
  }, [active, pathname]);
}

/** A scanned labour barcode opens the work order with ?op=N: bring that operation into view and mark it. */
function useScanTarget(enabled: boolean, pathname: string, op: string | null) {
  useEffect(() => {
    const seq = Number.parseInt(op ?? "", 10);
    if (!enabled || !workOrderKeyFromPath(pathname) || !Number.isFinite(seq) || seq <= 0) return;
    let done = false;
    let clear: number | undefined;
    const retries: number[] = [];
    const find = () => {
      if (done) return;
      const row = Array.from(document.querySelectorAll<HTMLElement>('[id^="wo-op-row-"]')).find((el) => sameOperation(el.id.slice("wo-op-row-".length), seq));
      if (!row) return;
      done = true;
      row.setAttribute("data-scan-target", "");
      row.scrollIntoView({ block: "center" });
      row.focus({ preventScroll: true });
      // Panels above it may still be loading and push it down: bring it back into view once they have settled.
      for (const ms of [500, 1500]) retries.push(window.setTimeout(() => row.isConnected && row.scrollIntoView({ block: "center" }), ms));
      clear = window.setTimeout(() => row.removeAttribute("data-scan-target"), 6000);
    };
    find();
    const observer = new MutationObserver(find);
    if (!done) observer.observe(document.body, { childList: true, subtree: true });
    const stop = window.setTimeout(() => observer.disconnect(), 15_000);
    return () => {
      observer.disconnect();
      window.clearTimeout(stop);
      retries.forEach((r) => window.clearTimeout(r));
      if (clear) window.clearTimeout(clear);
    };
  }, [enabled, pathname, op]);
}

/** "Offline — showing data from 14:32", no writes, and the answers already on the phone kept in memory. */
function OfflineLayer({ phoneMode }: { phoneMode: boolean }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const { principal } = useAuth();
  const roles = useShownRoles();
  const { pathname } = useLocation();
  const recent = useRef<string[]>([]);
  const keeper = useRef<OfflineKeeper | null>(null);
  const [dataAt, setDataAt] = useState<number | null>(null);
  const owner = principal?.userPrincipalName ?? principal?.displayName ?? null;
  const screens = screensFor(roles).map((s) => s.to);
  const canMyWork = screens.includes("/my/work");
  const canDispatch = screens.includes("/shop-floor/dispatch");

  // No change is ever sent or queued while offline.
  useEffect(() => {
    neverQueueMutations(queryClient);
    return installOfflineWriteGuard(apiClient, isOnline);
  }, [queryClient]);

  // A different person (or nobody) on this phone: forget the kept list; AuthContext has already cleared the answers.
  useEffect(() => {
    recent.current = [];
    keeper.current?.dispose();
    keeper.current = owner ? new OfflineKeeper(queryClient) : null;
    return () => {
      keeper.current?.dispose();
      keeper.current = null;
    };
  }, [owner, queryClient]);

  // Keep the last work orders opened, "My work" and the dispatch list.
  useEffect(() => {
    const key = workOrderKeyFromPath(pathname);
    if (key) recent.current = rememberWorkOrder(recent.current, key);
    keeper.current?.sync(keptQueryKeys(recent.current, { myWork: canMyWork, dispatch: canDispatch }));
  }, [pathname, canMyWork, canDispatch, owner]);

  // On the phone, fetch "My work" and the dispatch list while there is a connection (only when out of date).
  useEffect(() => {
    if (!phoneMode || !online || !owner) return;
    const prefetch = () => {
      // The API modules load on demand, so phone mode never pulls page code into the first download.
      if (canMyWork)
        void import("../../api/peopleApi")
          .then(({ peopleApi }) => queryClient.prefetchQuery({ queryKey: MY_WORK_KEY, queryFn: () => peopleApi.getMyWork(), staleTime: 5 * 60_000 }))
          .catch(() => undefined);
      if (canDispatch)
        void import("../../api/floorApi")
          .then(({ floorApi }) => queryClient.prefetchQuery({ queryKey: DISPATCH_KEY, queryFn: () => floorApi.getDispatchBoardByRule({ rule: "CriticalRatio" }), staleTime: 5 * 60_000 }))
          .catch(() => undefined);
    };
    prefetch();
    const t = window.setInterval(prefetch, 10 * 60_000);
    return () => window.clearInterval(t);
  }, [phoneMode, online, owner, canMyWork, canDispatch, queryClient]);

  // The banner, the page shifted under it, and every submit button off while offline.
  useEffect(() => {
    const root = document.documentElement;
    const main = document.getElementById("main");
    if (online) {
      root.removeAttribute("data-offline");
      if (main) setWriteControlsDisabled(main, false);
      return;
    }
    root.setAttribute("data-offline", "");
    setDataAt(shownDataAt(queryClient));
    if (!main) return;
    setWriteControlsDisabled(main, true);
    const observer = new MutationObserver(() => setWriteControlsDisabled(main, true));
    observer.observe(main, { childList: true, subtree: true });
    const onSubmit = (e: Event) => {
      if (main.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("submit", onSubmit, true);
    return () => {
      observer.disconnect();
      document.removeEventListener("submit", onSubmit, true);
      root.removeAttribute("data-offline");
      setWriteControlsDisabled(main, false);
    };
  }, [online, pathname, queryClient]);

  if (online) return null;
  return (
    <div
      role="status"
      className="fixed inset-x-0 top-12 z-[35] flex items-center justify-center gap-2 border-b border-amber-300 bg-amber-50 px-3 py-2 text-center text-sm font-medium text-amber-900 shadow-sm print:hidden"
      data-testid="offline-banner"
    >
      <WifiOff aria-hidden="true" className="h-4 w-4 flex-none" />
      <span>
        {offlineBannerText(dataAt, getOfflineSince())}. Changes are off until you're back online.
      </span>
    </div>
  );
}

function PhoneShell() {
  const sheet = usePhoneSheet();
  const { pathname } = useLocation();
  useStackedTables(true, pathname);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-phone-layout", "");
    return () => root.removeAttribute("data-phone-layout");
  }, []);

  return (
    <>
      <BottomTabBar />
      <Suspense fallback={null}>
        {sheet?.kind === "scan" && <ScannerSheet open />}
        {sheet?.kind === "more" && <MoreSheet open />}
        {sheet?.kind === "report" && <ProblemReportSheet key={JSON.stringify(sheet.context)} open context={sheet.context} />}
      </Suspense>
    </>
  );
}

export function PhoneLayer() {
  const features = usePhoneFeatures();
  const phoneMode = usePhoneMode();
  const release150 = useRelease150();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  useScanTarget(release150 && features, pathname, params.get("op"));
  if (!features) return null;
  return (
    <>
      <OfflineLayer phoneMode={phoneMode} />
      {phoneMode && <PhoneShell />}
    </>
  );
}
